import React, { useMemo, useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Typography, List, ListItem, ListItemText, Checkbox, Card, CardHeader, CardContent, Button } from '@mui/material';
import useRoles from '../hooks/use-roles';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { selectIsLoading, updateRole } from '../features/roles/role.slice';
import Loader from '../components/loader';
import { Permission, Role, RoleRequest } from '../service/model/role.model';
import usePermissions from '../hooks/use-permissions';
import { selectLoggedInUser } from '../features/auth/auth.slice';
import { getPermissionsForLoggedInUser } from '../shared/utils';
import { PermissionCode } from '../shared/enums';

const Permissions: React.FC = () => {
    const [roles] = useRoles();
    const [permissions] = usePermissions();
    const isLoading = useAppSelector(selectIsLoading);

    const loggedInUser = useAppSelector(selectLoggedInUser);
    const loggedInUserPermissions = getPermissionsForLoggedInUser(loggedInUser);

    const dispatch = useAppDispatch();
    const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('');

    const selectedRole: Role | undefined = roles.find(r => r.id === selectedRoleId);
    const originalPermissionIds = selectedRole ? (selectedRole.permissions?.map(p => p.id) || []) : [];

    // track selected permission ids for the UI
    const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);

    useEffect(() => {
        if (selectedRole) {
            setSelectedPermissionIds(selectedRole.permissions?.map(p => p.id) || []);
        } else {
            setSelectedPermissionIds([]);
        }
    }, [selectedRole]);

    const handleChange = (event: SelectChangeEvent) => {
        const val = event.target.value as string;
        setSelectedRoleId(val === '' ? '' : Number(val));
    };

    const togglePermission = (id: number) => {
        setSelectedPermissionIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const arraysEqual = (a: number[], b: number[]) => {
        if (a.length !== b.length) return false;
        const sa = new Set(a);
        return b.every(x => sa.has(x));
    }

    const hasChanges = !arraysEqual(originalPermissionIds, selectedPermissionIds);

    const handleReset = () => {
        setSelectedPermissionIds(originalPermissionIds);
    };

    const handleSubmit = async () => {
        if (!selectedRole) return;
        const updatedRoleRequest: RoleRequest = {
            id: selectedRole.id,
            name: selectedRole.name,
            description: selectedRole.description,
            permissionIds: selectedPermissionIds,
        };

        await dispatch(updateRole(updatedRoleRequest));
    };

    const groupedPermissions = useMemo(() => {
        const map = new Map<string, typeof selectedRole extends Role ? Role['permissions'] : any>();
        if (!selectedRole) return map;
        permissions.forEach((p) => {
            const moduleName = p.module || 'default';
            const list = map.get(moduleName) || [];
            list.push(p);
            map.set(moduleName, list);
        });
        return map;
    }, [permissions, selectedRole]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Permissions</Typography>

            <FormControl fullWidth sx={{ mb: 3, maxWidth: 300 }}>
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                    labelId="role-select-label"
                    id="role-select"
                    value={selectedRoleId === '' ? '' : String(selectedRoleId)}
                    label="Role"
                    onChange={handleChange}
                >
                    <MenuItem value="">-- Select role --</MenuItem>
                    {[...roles]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((role) => (
                            <MenuItem key={role.id} value={String(role.id)}>
                                {role.displayName || role.name}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            {!selectedRole && (
                <Typography variant="body1">Please select a role to view permissions.</Typography>
            )}

            {selectedRole && (
                <Box>
                    <Box sx={{
                        columnCount: { xs: 1, md: 3 },
                        columnGap: '16px',
                    }}>
                        {Array.from(groupedPermissions.entries()).map(([moduleName]) => (
                            <Card
                                key={moduleName}
                                sx={{
                                    display: 'inline-block',
                                    width: '100%',
                                    mb: 2,
                                    p: 2,
                                    breakInside: 'avoid',
                                    WebkitColumnBreakInside: 'avoid',
                                }}
                            >
                                <CardHeader title={moduleName} sx={{ pb: 0 }} />
                                <CardContent sx={{ pt: 0 }}>
                                    <List>
                                        {permissions.filter(p => p.module === moduleName).map((p: Permission) => (
                                            <ListItem key={p.id} sx={{ height: 'fit-content' }} disablePadding>
                                                <Checkbox
                                                    checked={selectedPermissionIds.includes(p.id)}
                                                    disabled={!loggedInUserPermissions.includes(PermissionCode.ROLES_EDIT) || selectedRole.name === 'ROLE_OWNER'}
                                                    onChange={() => togglePermission(p.id)}
                                                />
                                                <ListItemText primary={p.action} secondary={p.description} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        ))}

                        {groupedPermissions.size === 0 && (
                            <Typography variant="body2">No permissions defined for this role.</Typography>
                        )}
                    </Box>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button variant="outlined" onClick={handleReset} disabled={!selectedRole || !hasChanges || !loggedInUserPermissions.includes(PermissionCode.ROLES_EDIT)}>Reset</Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={!selectedRole || !hasChanges || !loggedInUserPermissions.includes(PermissionCode.ROLES_EDIT)}>Submit</Button>
                    </Box>
                 </Box>
             )}
         </Box>
     );
 };

export default Permissions;
